import ColorizeIcon from "@mui/icons-material/Colorize";
import {
  Box,
  IconButton,
  InputAdornment,
  Popover,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dico from "../../../util/src/language/dico";
import {
  clamp,
  composeHexColorWithAlpha,
  toColorAlpha,
  toColorPickerValue,
} from "../../../util/src/blueprint/index.js";
import { EDITOR_PICKER_NESTED_POPOVER_Z_INDEX } from "./editorPickerPopoverZIndex";

type ColorPickerProps = {
  /** Current CSS color. `null` / `undefined` / empty → opaque default. */
  value: string | null | undefined;
  disabled?: boolean;
  onChange: (nextColor: string) => void;
};

/**
 * Colors that represent “no color chosen” rather than an intentional paint.
 * For these, the picker exposes opacity at 100% so the first color pick is visible.
 */
const isNullLikeColorValue = (raw: unknown): boolean => {
  if (raw == null) return true;
  if (typeof raw !== "string") return true;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return true;
  if (trimmed === "transparent") return true;
  // Fully transparent black used as empty defaults (e.g. box/text shadow).
  if (trimmed === "#0000" || trimmed === "#00000000") return true;
  return false;
};

type ColorMode = "hex" | "rgb" | "hsl";

type Rgb = { r: number; g: number; b: number };
type Hsl = { h: number; s: number; l: number };
type Hsv = { h: number; s: number; v: number };

const hexToRgb = (hex: string): Rgb => {
  const normalized = toColorPickerValue(hex).replace("#", "");
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) =>
    clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsl = (r: number, g: number, b: number): Hsl => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const l = (max + min) / 2;

  if (d === 0) return { h: 0, s: 0, l: Math.round(l * 100) };

  const s = d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  switch (max) {
    case rn:
      h = ((gn - bn) / d) % 6;
      break;
    case gn:
      h = (bn - rn) / d + 2;
      break;
    default:
      h = (rn - gn) / d + 4;
      break;
  }

  h = Math.round(h * 60);
  if (h < 0) h += 360;
  return { h, s: Math.round(s * 100), l: Math.round(l * 100) };
};

const hslToRgb = (h: number, s: number, l: number): Rgb => {
  const hn = ((h % 360) + 360) % 360;
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = ln - c / 2;
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (hn < 60) {
    r1 = c;
    g1 = x;
  } else if (hn < 120) {
    r1 = x;
    g1 = c;
  } else if (hn < 180) {
    g1 = c;
    b1 = x;
  } else if (hn < 240) {
    g1 = x;
    b1 = c;
  } else if (hn < 300) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
};

const rgbToHsv = (r: number, g: number, b: number): Hsv => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (d !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / d) % 6;
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h: Math.round(h), s: Math.round(s * 100), v: Math.round(max * 100) };
};

const hsvToRgb = (h: number, s: number, v: number): Rgb => {
  const hn = ((h % 360) + 360) % 360;
  const sn = clamp(s, 0, 100) / 100;
  const vn = clamp(v, 0, 100) / 100;
  const c = vn * sn;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = vn - c;
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hn < 60) {
    r1 = c;
    g1 = x;
  } else if (hn < 120) {
    r1 = x;
    g1 = c;
  } else if (hn < 180) {
    g1 = c;
    b1 = x;
  } else if (hn < 240) {
    g1 = x;
    b1 = c;
  } else if (hn < 300) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
};

/**
 * Detects whether a raw color string is expressed with an explicit alpha
 * channel (hex with 4/8 hex chars, rgba/hsla, `transparent` keyword, modern
 * CSS color functions using the `/ alpha` slash syntax). When true, the alpha
 * extracted from the string should override the picker's current alpha.
 * Otherwise the picker preserves the previous alpha value.
 */
const hasExplicitAlphaChannel = (raw: string): boolean => {
  const candidate = raw.trim().toLowerCase();
  if (!candidate) return false;
  if (candidate === "transparent") return true;
  if (/^#(?:[\da-f]{4}|[\da-f]{8})$/i.test(candidate)) return true;
  if (/^rgba\(/i.test(candidate)) return true;
  if (/^hsla\(/i.test(candidate)) return true;
  // `rgb(... / a)`, `hsl(... / a)`, `oklch(... / a)`, etc.
  if (/\)\s*$/.test(candidate) && /\/\s*[\d.]+%?\s*\)$/i.test(candidate))
    return true;
  return false;
};

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<ColorMode>("rgb");
  const [isPickingFromScreen, setIsPickingFromScreen] = useState(false);
  const labels =
    dico().licensedPortal?.layoutEditor?.colorPicker ??
    ({} as Record<string, string>);

  const baseColor = useMemo(() => toColorPickerValue(value ?? ""), [value]);
  const alpha = useMemo(() => {
    // Null-like / absent colors must open with full opacity (not 0).
    if (isNullLikeColorValue(value)) return 100;
    return toColorAlpha(value);
  }, [value]);
  // Local draft for the free-form text field (hex mode). We let the user type
  // any CSS color (named keyword, rgb(...), hex…) without committing on every
  // keystroke; the value is normalized via `toColorPickerValue` on blur/Enter.
  const [textDraft, setTextDraft] = useState<string | null>(null);
  const rgb = useMemo(() => hexToRgb(baseColor), [baseColor]);
  const hsl = useMemo(
    () => rgbToHsl(rgb.r, rgb.g, rgb.b),
    [rgb.r, rgb.g, rgb.b],
  );
  const rawHsv = useMemo(
    () => rgbToHsv(rgb.r, rgb.g, rgb.b),
    [rgb.r, rgb.g, rgb.b],
  );
  const lastHueRef = useRef(rawHsv.h);
  const [hueDraft, setHueDraft] = useState(rawHsv.h);
  const hsv = useMemo(() => {
    if (rawHsv.s > 0) {
      lastHueRef.current = rawHsv.h;
      return rawHsv;
    }
    return { ...rawHsv, h: lastHueRef.current };
  }, [rawHsv]);
  const effectiveHue = rawHsv.s > 0 ? rawHsv.h : hueDraft;

  const updateColor = useCallback(
    (nextColor: string, nextAlpha: number) => {
      onChange(composeHexColorWithAlpha(nextColor, clamp(nextAlpha, 0, 100)));
    },
    [onChange],
  );

  const commitTextDraft = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) {
        setTextDraft(null);
        return;
      }
      const nextHex = toColorPickerValue(trimmed);
      const explicitAlpha = hasExplicitAlphaChannel(trimmed);
      const nextAlpha = explicitAlpha ? toColorAlpha(trimmed) : alpha;
      updateColor(nextHex, nextAlpha);
      setTextDraft(null);
    },
    [alpha, updateColor],
  );

  const commitHsv = (nextH: number, nextS: number, nextV: number) => {
    const nextRgb = hsvToRgb(nextH, nextS, nextV);
    updateColor(rgbToHex(nextRgb.r, nextRgb.g, nextRgb.b), alpha);
  };

  const popupOpen = Boolean(anchorEl);
  const dragSeqRef = useRef(0);
  const saturationMoveListenerRef = useRef<
    ((event: MouseEvent) => void) | null
  >(null);
  const saturationUpListenerRef = useRef<(() => void) | null>(null);

  const clearSaturationDragListeners = useCallback(() => {
    if (saturationMoveListenerRef.current) {
      window.removeEventListener(
        "mousemove",
        saturationMoveListenerRef.current,
      );
      saturationMoveListenerRef.current = null;
    }
    if (saturationUpListenerRef.current) {
      window.removeEventListener("mouseup", saturationUpListenerRef.current);
      window.removeEventListener("mouseleave", saturationUpListenerRef.current);
      window.removeEventListener("blur", saturationUpListenerRef.current);
      saturationUpListenerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!popupOpen) {
      clearSaturationDragListeners();
    }
  }, [clearSaturationDragListeners, popupOpen]);

  useEffect(() => clearSaturationDragListeners, [clearSaturationDragListeners]);
  useEffect(() => {
    if (rawHsv.s > 0) {
      setHueDraft(rawHsv.h);
    }
  }, [rawHsv.h, rawHsv.s]);
  useEffect(() => {
    if (!popupOpen) setTextDraft(null);
  }, [popupOpen]);
  useEffect(() => {
    setTextDraft(null);
  }, [baseColor]);

  const handlePickColorFromScreen = useCallback(async () => {
    if (disabled || isPickingFromScreen) return;
    const eyeDropperCtor = (
      window as Window & {
        EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> };
      }
    ).EyeDropper;
    if (!eyeDropperCtor) return;

    try {
      setIsPickingFromScreen(true);
      const eyeDropper = new eyeDropperCtor();
      const result = await eyeDropper.open();
      if (result?.sRGBHex) {
        updateColor(result.sRGBHex, alpha);
      }
    } catch {
      // User cancelled eyedropper or browser blocked it.
    } finally {
      setIsPickingFromScreen(false);
    }
  }, [alpha, disabled, isPickingFromScreen, updateColor]);

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={(e) => {
          if (!disabled) setAnchorEl(e.currentTarget as HTMLElement);
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setAnchorEl(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          height: 40,
          border: "1px solid",
          borderColor: popupOpen ? "primary.main" : "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          px: 1,
          gap: 1,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          "&:hover": {
            borderColor: disabled ? "rgba(0,0,0,0.23)" : "text.primary",
          },
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: 0.6,
            border: "1px solid rgba(0,0,0,0.2)",
            backgroundColor: baseColor,
            flexShrink: 0,
          }}
        />
        <Typography sx={{ flex: 1, fontFamily: "monospace", fontSize: 12 }}>
          {value?.trim() || baseColor}
        </Typography>
      </Box>

      <Popover
        open={popupOpen}
        anchorEl={anchorEl}
        onClose={() => {
          clearSaturationDragListeners();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            sx: { zIndex: EDITOR_PICKER_NESTED_POPOVER_Z_INDEX },
          },
        }}
      >
        <Box
          sx={{
            width: 308,
            p: 1,
            bgcolor: "#ffffff",
            color: "#1f2937",
            borderRadius: "12px",
          }}
        >
          <Box
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragSeqRef.current += 1;
              clearSaturationDragListeners();
              const target = e.currentTarget;
              const rect = target.getBoundingClientRect();
              const handleMove = (clientX: number, clientY: number) => {
                const x = clamp(clientX - rect.left, 0, rect.width);
                const y = clamp(clientY - rect.top, 0, rect.height);
                const nextS = (x / rect.width) * 100;
                const nextV = 100 - (y / rect.height) * 100;
                commitHsv(effectiveHue, nextS, nextV);
              };
              handleMove(e.clientX, e.clientY);
              const up = () => {
                clearSaturationDragListeners();
              };
              const move = (evt: MouseEvent) => {
                if ((evt.buttons & 1) !== 1) {
                  up();
                  return;
                }
                handleMove(evt.clientX, evt.clientY);
              };
              saturationMoveListenerRef.current = move;
              saturationUpListenerRef.current = up;
              window.addEventListener("mousemove", move);
              window.addEventListener("mouseup", up);
              window.addEventListener("mouseleave", up);
              window.addEventListener("blur", up);
            }}
            sx={{
              height: 136,
              borderRadius: "8px",
              position: "relative",
              cursor: "crosshair",
              backgroundColor: `hsl(${effectiveHue} 100% 50%)`,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to right, #fff, rgba(255,255,255,0))",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(to top, #000, rgba(0,0,0,0))",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                left: `calc(${hsv.s}% - 6px)`,
                top: `calc(${100 - hsv.v}% - 6px)`,
                width: 12,
                height: 12,
                borderRadius: "50%",
                border: "2px solid #fff",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.5)",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <IconButton
              size="small"
              onClick={handlePickColorFromScreen}
              disabled={disabled || isPickingFromScreen}
              sx={{
                width: 28,
                height: 28,
                border: "1px solid #c7d2e0",
                borderRadius: "6px",
                color: "#334155",
                "&:hover": {
                  borderColor: "#94a3b8",
                  backgroundColor: "#f1f5f9",
                },
              }}
            >
              <ColorizeIcon sx={{ fontSize: 17 }} />
            </IconButton>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                backgroundColor: baseColor,
                flexShrink: 0,
              }}
            />
            <Slider
              value={effectiveHue}
              min={0}
              max={360}
              step={1}
              onChange={(_, v) => {
                const nextHue = Number(v);
                setHueDraft(nextHue);
                lastHueRef.current = nextHue;
                commitHsv(nextHue, hsv.s, hsv.v);
              }}
              sx={{
                color: "transparent",
                flex: 1,
                p: 0,
                height: 14,
                "& .MuiSlider-rail": {
                  opacity: 1,
                  height: 12,
                  borderRadius: 1,
                  background:
                    "linear-gradient(90deg, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 66%, #ff00ff 83%, #ff0000 100%)",
                },
                "& .MuiSlider-track": { display: "none" },
                "& .MuiSlider-thumb": {
                  width: 14,
                  height: 14,
                  bgcolor: "#fff",
                  border: "2px solid #ff2b2b",
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            {(["hex", "rgb", "hsl"] as ColorMode[]).map((entry) => {
              const entryLabel =
                entry === "hex"
                  ? labels.hexMode
                  : entry === "rgb"
                    ? labels.rgbMode
                    : labels.hslMode;
              return (
                <Box
                  key={entry}
                  onClick={() => setMode(entry)}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: mode === entry ? "#4f9cff" : "#c7d2e0",
                    color: "#334155",
                    py: 0.45,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    userSelect: "none",
                    bgcolor:
                      mode === entry ? "rgba(79,156,255,0.15)" : "transparent",
                  }}
                >
                  {entryLabel ?? ""}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 1, display: "flex", gap: 0.75 }}>
            {mode === "hex" && (
              <TextField
                size="small"
                fullWidth
                value={textDraft ?? baseColor}
                onChange={(e) => setTextDraft(e.target.value)}
                onBlur={(e) => commitTextDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitTextDraft((e.target as HTMLInputElement).value);
                  } else if (e.key === "Escape") {
                    setTextDraft(null);
                  }
                }}
                placeholder={baseColor}
                sx={inputStyle}
              />
            )}

            {mode === "rgb" && (
              <>
                <TextField
                  size="small"
                  type="number"
                  value={rgb.r}
                  onChange={(e) =>
                    updateColor(
                      rgbToHex(Number(e.target.value), rgb.g, rgb.b),
                      alpha,
                    )
                  }
                  sx={inputStyle}
                  slotProps={{ htmlInput: { min: 0, max: 255, step: 1 } }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={rgb.g}
                  onChange={(e) =>
                    updateColor(
                      rgbToHex(rgb.r, Number(e.target.value), rgb.b),
                      alpha,
                    )
                  }
                  sx={inputStyle}
                  slotProps={{ htmlInput: { min: 0, max: 255, step: 1 } }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={rgb.b}
                  onChange={(e) =>
                    updateColor(
                      rgbToHex(rgb.r, rgb.g, Number(e.target.value)),
                      alpha,
                    )
                  }
                  sx={inputStyle}
                  slotProps={{ htmlInput: { min: 0, max: 255, step: 1 } }}
                />
              </>
            )}

            {mode === "hsl" && (
              <>
                <TextField
                  size="small"
                  type="number"
                  value={hsl.h}
                  onChange={(e) => {
                    const nextRgb = hslToRgb(
                      Number(e.target.value),
                      hsl.s,
                      hsl.l,
                    );
                    updateColor(
                      rgbToHex(nextRgb.r, nextRgb.g, nextRgb.b),
                      alpha,
                    );
                  }}
                  sx={inputStyle}
                  slotProps={{ htmlInput: { min: 0, max: 360, step: 1 } }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={hsl.s}
                  onChange={(e) => {
                    const nextRgb = hslToRgb(
                      hsl.h,
                      Number(e.target.value),
                      hsl.l,
                    );
                    updateColor(
                      rgbToHex(nextRgb.r, nextRgb.g, nextRgb.b),
                      alpha,
                    );
                  }}
                  sx={inputStyle}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    },
                    htmlInput: { min: 0, max: 100, step: 1 },
                  }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={hsl.l}
                  onChange={(e) => {
                    const nextRgb = hslToRgb(
                      hsl.h,
                      hsl.s,
                      Number(e.target.value),
                    );
                    updateColor(
                      rgbToHex(nextRgb.r, nextRgb.g, nextRgb.b),
                      alpha,
                    );
                  }}
                  sx={inputStyle}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    },
                    htmlInput: { min: 0, max: 100, step: 1 },
                  }}
                />
              </>
            )}
          </Box>

          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
              {labels.opacity ?? ""}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Slider
                value={alpha}
                min={0}
                max={100}
                step={1}
                onChange={(_, v) => updateColor(baseColor, Number(v))}
                sx={{
                  flex: 1,
                  maxWidth: 165,
                  px: 0.75,
                  color: "#4f9cff",
                  "& .MuiSlider-rail": { opacity: 0.35 },
                  "& .MuiSlider-thumb": {
                    width: 14,
                    height: 14,
                  },
                }}
              />
              <TextField
                size="small"
                fullWidth
                type="number"
                value={alpha}
                onChange={(e) => updateColor(baseColor, Number(e.target.value))}
                sx={{ ...inputStyle, width: 100, flex: "unset" }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                  htmlInput: { min: 0, max: 100, step: 1 },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const inputStyle = {
  flex: 1,
  "& .MuiInputBase-root": {
    bgcolor: "#ffffff",
    color: "#1f2937",
    borderRadius: 0.5,
    fontFamily: "monospace",
    fontSize: 13,
    height: 38,
    "& fieldset": { borderColor: "#c7d2e0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#4f9cff" },
  },
  "& .MuiInputAdornment-root": { color: "#64748b", fontSize: 12 },
};

export default ColorPicker;
